import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/gemora/ProductCard";
import CategoryBanner from "@/components/gemora/CategoryBanner";
import { Funnel } from "@phosphor-icons/react";

const CATS = ["gemstone", "rudraksha", "bracelet", "yantra", "idol", "pooja_kit", "prashad", "book"];
const PLANETS = ["Sun", "Moon", "Mars", "Mercury", "Jupiter", "Venus", "Saturn", "Rahu", "Ketu"];
// Fallback until the admin-editable taxonomy loads from /categories.
const DEFAULT_PURPOSES = [
  { key: "wealth", label: "Wealth" }, { key: "protection", label: "Protection" },
  { key: "love", label: "Love" }, { key: "career", label: "Career" }, { key: "health", label: "Health" },
];

export default function Shop() {
  const [sp, setSp] = useSearchParams();
  const category = sp.get("category") || "";
  const graha = sp.get("graha") || "";
  const purpose = sp.get("purpose") || "";
  const rashi = sp.get("rashi") || "";
  const q = sp.get("q") || "";
  const price = sp.get("price") || "";
  const mukhi = sp.get("mukhi") || "";
  const subcategory = sp.get("subcategory") || "";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purposes, setPurposes] = useState(DEFAULT_PURPOSES);
  const [rashiList, setRashiList] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    // Purposes, Rashi & the category tree (incl. subcategories) are admin-editable
    // (Admin → Website / Admin → Categories); load the current lists.
    api.get("/categories").then(({ data }) => {
      if (data.purposes?.length) setPurposes(data.purposes);
      if (data.rashi?.length) setRashiList(data.rashi);
      if (data.categories?.length) setCategories(data.categories);
    }).catch(() => {});
  }, []);

  // Subcategories of the currently selected top-level category, if any exist.
  const subcats = useMemo(
    () => categories.filter((c) => c.category_key === category && c.parent_category_id),
    [categories, category]
  );

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (category) p.set("category", category);
    if (subcategory) p.set("subcategory", subcategory);
    if (graha) p.set("graha", graha);
    if (purpose) p.set("purpose", purpose);
    if (rashi) p.set("rashi", rashi);
    if (q) p.set("q", q);
    if (price) p.set("price", price);
    if (mukhi) p.set("mukhi", mukhi);
    return p.toString();
  }, [category, subcategory, graha, purpose, rashi, q, price, mukhi]);

  // Polled so new/changed products, prices and stock show up without a refresh;
  // paused while the tab is hidden so background tabs don't keep hammering the API.
  // Only the initial/filter-change fetch shows the loading state — a background
  // poll refresh must not blank the grid out from under someone browsing it.
  useEffect(() => {
    setLoading(true);
    api.get(`/products?${params}`).then(({ data }) => setItems(data)).finally(() => setLoading(false));
    const t = setInterval(() => {
      if (document.hidden) return;
      api.get(`/products?${params}`).then(({ data }) => setItems(data)).catch(() => {});
    }, 60_000);
    return () => clearInterval(t);
  }, [params]);

  const setFilter = (k, v) => {
    const n = new URLSearchParams(sp);
    if (v) n.set(k, v); else n.delete(k);
    if (k === "category") n.delete("subcategory"); // stale sub from a different category
    setSp(n);
  };

  // A single-category view gets the full editorial collection banner; search and
  // the all-goods view keep the plain header.
  const showBanner = category && !q;

  return (
    <div>
      {/* Full-bleed hero collection banner (single-category view) */}
      {showBanner && <CategoryBanner category={category} />}

      <div className="mx-auto max-w-[1600px] px-6 lg:px-10 py-12">
      {!showBanner && (
        <header className="mb-10">
          <div className="text-xs uppercase tracking-[0.3em] text-gold-soft">The Store · भंडार</div>
          <h1 className="font-display text-4xl md:text-5xl text-ink mt-3">
            {q ? <>Search — <em className="font-serifd">"{q}"</em></> : "All authentic goods"}
          </h1>
        </header>
      )}

      <div id="collection-grid" className="grid lg:grid-cols-[260px_1fr] gap-8 scroll-mt-24">
        <aside className="lg:sticky lg:top-24 h-fit">
          <div className="gold-line-strong p-6">
            <div className="flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-ink pb-4 border-b border-gold/20"><Funnel size={18} weight="duotone" className="text-gold-soft" /> Filters</div>
            <div className="mt-6">
              <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted mb-3">Category</div>
              <div className="flex flex-wrap gap-2.5">
                {CATS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setFilter("category", category === c ? "" : c)}
                    data-testid={`filter-cat-${c}`}
                    className={`text-sm px-3.5 py-2 border transition-colors ${category === c ? "bg-maroon text-ivory border-maroon" : "border-gold/40 text-ink-soft hover:border-maroon"}`}
                  >
                    {c.replace("_", " ")}
                  </button>
                ))}
              </div>
            </div>
            {subcats.length > 0 && (
              <div className="mt-7">
                <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted mb-3">Type</div>
                <div className="flex flex-wrap gap-2.5">
                  {subcats.map((s) => (
                    <button
                      key={s.category_id}
                      onClick={() => setFilter("subcategory", subcategory === s.slug ? "" : s.slug)}
                      data-testid={`filter-subcat-${s.slug}`}
                      className={`text-sm px-3.5 py-2 border transition-colors ${subcategory === s.slug ? "bg-maroon text-ivory border-maroon" : "border-gold/40 text-ink-soft hover:border-maroon"}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-7">
              <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted mb-3">Planet</div>
              <div className="flex flex-wrap gap-2.5">
                {PLANETS.map((p) => (
                  <button
                    key={p}
                    onClick={() => setFilter("graha", graha === p ? "" : p)}
                    className={`text-sm px-3.5 py-2 border transition-colors ${graha === p ? "bg-maroon text-ivory border-maroon" : "border-gold/40 text-ink-soft hover:border-maroon"}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-7">
              <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted mb-3">Purpose</div>
              <div className="flex flex-wrap gap-2.5">
                {purposes.map((p) => (
                  <button
                    key={p.key}
                    onClick={() => setFilter("purpose", purpose === p.key ? "" : p.key)}
                    className={`text-sm px-3.5 py-2 border transition-colors ${purpose === p.key ? "bg-maroon text-ivory border-maroon" : "border-gold/40 text-ink-soft hover:border-maroon"}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            {rashiList.length > 0 && (
              <div className="mt-7">
                <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted mb-3">Rashi</div>
                <div className="flex flex-wrap gap-2.5">
                  {rashiList.map((r) => {
                    const chipCls = `text-sm px-3.5 py-2 border transition-colors ${rashi === r.key ? "bg-maroon text-ivory border-maroon" : "border-gold/40 text-ink-soft hover:border-maroon"}`;
                    // A mapped rashi (stone + page URL, set under Admin → Website → Rashi)
                    // sends buyers straight to that page instead of just filtering in place.
                    if (r.url?.trim()) {
                      const external = /^https?:\/\//i.test(r.url);
                      return external ? (
                        <a key={r.key} href={r.url} target="_blank" rel="noreferrer" className={chipCls}>{r.label}</a>
                      ) : (
                        <Link key={r.key} to={r.url} className={chipCls}>{r.label}</Link>
                      );
                    }
                    return (
                      <button
                        key={r.key}
                        onClick={() => setFilter("rashi", rashi === r.key ? "" : r.key)}
                        className={chipCls}
                      >
                        {r.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </aside>

        <div>
          {loading ? (
            <div className="text-ink-muted">Loading…</div>
          ) : items.length === 0 ? (
            <div className="gold-line p-10 text-center text-ink-muted">No goods match those filters. Try widening.</div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-5">
              {items.map((p) => <ProductCard key={p.product_id} p={p} />)}
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
